import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/erp`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const erpApi = {
  // Admin Dashboard
  admin: {
    getDashboard: () => axios.get(`${API_BASE}/admin/dashboard`, { headers: getAuthHeaders() }),
    getRevenueChart: (months = 6) => axios.get(`${API_BASE}/admin/charts/revenue`, { params: { months }, headers: getAuthHeaders() }),
    getProductionChart: () => axios.get(`${API_BASE}/admin/charts/production`, { headers: getAuthHeaders() }),
    getExpenseChart: () => axios.get(`${API_BASE}/admin/charts/expenses`, { headers: getAuthHeaders() }),
    getTopCustomers: () => axios.get(`${API_BASE}/admin/charts/top-customers`, { headers: getAuthHeaders() }),
  },
  
  // CRM
  crm: {
    createLead: (data) => axios.post(`${API_BASE}/crm/leads/create`, data, { headers: getAuthHeaders() }),
    getLeads: (params) => axios.get(`${API_BASE}/crm/leads`, { params, headers: getAuthHeaders() }),
    updateLead: (id, data) => axios.put(`${API_BASE}/crm/leads/${id}`, data, { headers: getAuthHeaders() }),
    updateLeadStatus: (id, status) => axios.patch(`${API_BASE}/crm/leads/${id}/status`, { status }, { headers: getAuthHeaders() }),
  },
  
  // Production
  production: {
    createOrder: (data) => axios.post(`${API_BASE}/production/orders`, data, { headers: getAuthHeaders() }),
    getOrders: (params) => axios.get(`${API_BASE}/production/orders`, { params, headers: getAuthHeaders() }),
    updateStatus: (id, stage) => axios.patch(`${API_BASE}/production/orders/${id}/stage`, { stage }, { headers: getAuthHeaders() }),
    createBreakage: (data) => axios.post(`${API_BASE}/production/breakage`, data, { headers: getAuthHeaders() }),
    getBreakageAnalytics: (params) => axios.get(`${API_BASE}/production/breakage/analytics`, { params, headers: getAuthHeaders() }),
    getPendingOrders: () => axios.get(`${API_BASE}/production/pending-orders`, { headers: getAuthHeaders() }),
    createJobCardFromOrder: (orderId) => axios.post(`${API_BASE}/production/job-card/${orderId}`, {}, { headers: getAuthHeaders() }),
  },
  
  // HR
  hr: {
    createEmployee: (data) => axios.post(`${API_BASE}/hr/employees`, data, { headers: getAuthHeaders() }),
    getEmployees: () => axios.get(`${API_BASE}/hr/employees`, { headers: getAuthHeaders() }),
    getEmployee: (id) => axios.get(`${API_BASE}/hr/employees/${id}`, { headers: getAuthHeaders() }),
    updateEmployee: (id, data) => axios.put(`${API_BASE}/hr/employees/${id}`, data, { headers: getAuthHeaders() }),
    getSalaryHistory: (id) => axios.get(`${API_BASE}/hr/employees/${id}/salary-history`, { headers: getAuthHeaders() }),
    updateEmployeeSalary: (id, data) => axios.post(`${API_BASE}/hr/employees/${id}/salary`, data, { headers: getAuthHeaders() }),
    getSalaries: (params) => axios.get(`${API_BASE}/hr/salary`, { params, headers: getAuthHeaders() }),
    calculateSalary: (empId, data) => axios.post(`${API_BASE}/hr/salary/calculate/${empId}`, data, { headers: getAuthHeaders() }),
    approveSalary: (salaryId) => axios.post(`${API_BASE}/hr/salary/approve/${salaryId}`, {}, { headers: getAuthHeaders() }),
    // Attendance
    markAttendance: (data) => axios.post(`${API_BASE}/hr/attendance`, data, { headers: getAuthHeaders() }),
    getAttendance: (params) => axios.get(`${API_BASE}/hr/attendance`, { params, headers: getAuthHeaders() }),
    getAttendanceSummary: (month) => axios.get(`${API_BASE}/hr/attendance/summary`, { params: { month }, headers: getAuthHeaders() }),
  },
  
  // File Upload
  upload: {
    uploadFile: (formData) => axios.post(`${API_BASE}/upload/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() }
    }),
  },

  // Accounts
  accounts: {
    getDashboard: () => axios.get(`${API_BASE}/accounts/dashboard`, { headers: getAuthHeaders() }),
    createInvoice: (data) => axios.post(`${API_BASE}/accounts/invoices`, data, { headers: getAuthHeaders() }),
    getInvoices: (params) => axios.get(`${API_BASE}/accounts/invoices`, { params, headers: getAuthHeaders() }),
    getInvoice: (id) => axios.get(`${API_BASE}/accounts/invoices/${id}`, { headers: getAuthHeaders() }),
    recordPayment: (invoiceId, data) => axios.post(`${API_BASE}/accounts/invoices/${invoiceId}/payment`, data, { headers: getAuthHeaders() }),
    getLedger: (params) => axios.get(`${API_BASE}/accounts/ledger`, { params, headers: getAuthHeaders() }),
    getGSTReport: (month) => axios.get(`${API_BASE}/accounts/gst-report`, { params: { month }, headers: getAuthHeaders() }),
    getProfitLoss: (startDate, endDate) => axios.get(`${API_BASE}/accounts/profit-loss`, { params: { start_date: startDate, end_date: endDate }, headers: getAuthHeaders() }),
  },

  // Receipts
  receipts: {
    create: (data) => axios.post(`${API_BASE}/receipts`, data, { headers: getAuthHeaders() }),
    list: (params) => axios.get(`${API_BASE}/receipts`, { params, headers: getAuthHeaders() }),
    get: (id) => axios.get(`${API_BASE}/receipts/${id}`, { headers: getAuthHeaders() }),
    byInvoice: (invoiceId) => axios.get(`${API_BASE}/receipts/invoice/${invoiceId}`, { headers: getAuthHeaders() }),
    byOrder: (orderId) => axios.get(`${API_BASE}/receipts/order/${orderId}`, { headers: getAuthHeaders() }),
    remove: (id) => axios.delete(`${API_BASE}/receipts/${id}`, { headers: getAuthHeaders() }),
  },

  // Billing Reports - Legally Compliant
  billingReports: {
    getCashSales: (params) => axios.get(`${API_BASE}/billing-reports/cash-sales`, { params, headers: getAuthHeaders() }),
    getBankSales: (params) => axios.get(`${API_BASE}/billing-reports/bank-sales`, { params, headers: getAuthHeaders() }),
    getGstSales: (params) => axios.get(`${API_BASE}/billing-reports/gst-sales`, { params, headers: getAuthHeaders() }),
    getB2bB2c: (params) => axios.get(`${API_BASE}/billing-reports/b2b-b2c`, { params, headers: getAuthHeaders() }),
    getPartyLedger: (customerId, params) => axios.get(`${API_BASE}/billing-reports/party-ledger/${customerId}`, { params, headers: getAuthHeaders() }),
    getPartyLedgerSummary: () => axios.get(`${API_BASE}/billing-reports/party-ledger-summary`, { headers: getAuthHeaders() }),
    getGstr1Export: (month) => axios.get(`${API_BASE}/billing-reports/gstr1-export`, { params: { month }, headers: getAuthHeaders() }),
    getCashReceipt: (paymentId) => axios.get(`${API_BASE}/billing-reports/cash-receipt/${paymentId}`, { headers: getAuthHeaders() }),
    getBankReceipt: (paymentId) => axios.get(`${API_BASE}/billing-reports/bank-receipt/${paymentId}`, { headers: getAuthHeaders() }),
    // New dashboard endpoints
    getGstVsCashDashboard: (params) => axios.get(`${API_BASE}/billing-reports/gst-vs-cash-dashboard`, { params, headers: getAuthHeaders() }),
    getDetailedLedger: (params) => axios.get(`${API_BASE}/billing-reports/detailed-ledger`, { params, headers: getAuthHeaders() }),
    getInvoiceFlow: (params) => axios.get(`${API_BASE}/billing-reports/invoice-flow`, { params, headers: getAuthHeaders() }),
  },
  
  // Inventory
  inventory: {
    getMaterials: (params) => axios.get(`${API_BASE}/inventory/materials`, { params, headers: getAuthHeaders() }),
    getMaterial: (id) => axios.get(`${API_BASE}/inventory/materials/${id}`, { headers: getAuthHeaders() }),
    createMaterial: (data) => axios.post(`${API_BASE}/inventory/materials`, data, { headers: getAuthHeaders() }),
    updateMaterial: (id, data) => axios.patch(`${API_BASE}/inventory/materials/${id}`, data, { headers: getAuthHeaders() }),
    addTransaction: (data) => axios.post(`${API_BASE}/inventory/transactions`, data, { headers: getAuthHeaders() }),
    getTransactions: (params) => axios.get(`${API_BASE}/inventory/transactions`, { params, headers: getAuthHeaders() }),
    getLowStock: () => axios.get(`${API_BASE}/inventory/low-stock`, { headers: getAuthHeaders() }),
  },
  
  // Purchase
  purchase: {
    createPO: (data) => axios.post(`${API_BASE}/purchase/orders`, data, { headers: getAuthHeaders() }),
    getPOs: (params) => axios.get(`${API_BASE}/purchase/orders`, { params, headers: getAuthHeaders() }),
    getPO: (id) => axios.get(`${API_BASE}/purchase/orders/${id}`, { headers: getAuthHeaders() }),
    updatePOStatus: (id, status) => axios.patch(`${API_BASE}/purchase/orders/${id}/status`, { status }, { headers: getAuthHeaders() }),
    createSupplier: (data) => axios.post(`${API_BASE}/purchase/suppliers`, data, { headers: getAuthHeaders() }),
    getSuppliers: () => axios.get(`${API_BASE}/purchase/suppliers`, { headers: getAuthHeaders() }),
  },

  // Payouts (Salary Disbursement)
  payouts: {
    getDashboard: () => axios.get(`${API_BASE}/payouts/dashboard`, { headers: getAuthHeaders() }),
    // Fund Accounts (Employee Bank Details)
    createFundAccount: (data) => axios.post(`${API_BASE}/payouts/fund-accounts`, data, { headers: getAuthHeaders() }),
    getFundAccounts: (params) => axios.get(`${API_BASE}/payouts/fund-accounts`, { params, headers: getAuthHeaders() }),
    // Salary Payouts
    processSalaryPayout: (data) => axios.post(`${API_BASE}/payouts/salary/process`, data, { headers: getAuthHeaders() }),
    bulkProcessPayouts: (data) => axios.post(`${API_BASE}/payouts/salary/bulk-process`, data, { headers: getAuthHeaders() }),
    getPayoutStatus: (salaryId) => axios.get(`${API_BASE}/payouts/salary/status/${salaryId}`, { headers: getAuthHeaders() }),
    // History
    getPayoutHistory: (params) => axios.get(`${API_BASE}/payouts/history`, { params, headers: getAuthHeaders() }),
  },

  // QR Code & Print
  qr: {
    getJobCardQR: (jobCardNumber) => `${API_BASE}/qr/job-card/${jobCardNumber}`,
    getJobCardBarcode: (jobCardNumber) => `${API_BASE}/qr/job-card/${jobCardNumber}/barcode`,
    getJobCardPrintData: (jobCardNumber) => axios.get(`${API_BASE}/qr/job-card/${jobCardNumber}/print-data`, { headers: getAuthHeaders() }),
    getInvoiceQR: (invoiceNumber) => `${API_BASE}/qr/invoice/${invoiceNumber}`,
    getOrderQR: (orderId) => `${API_BASE}/qr/order/${orderId}`,
  },

  // SMS & WhatsApp Notifications
  notifications: {
    getStatus: () => axios.get(`${API_BASE}/notifications/status`, { headers: getAuthHeaders() }),
    getSettings: () => axios.get(`${API_BASE}/notifications/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/notifications/settings`, data, { headers: getAuthHeaders() }),
    getLogs: (params) => axios.get(`${API_BASE}/notifications/logs`, { params, headers: getAuthHeaders() }),
    sendSMS: (data) => axios.post(`${API_BASE}/notifications/send-sms`, data, { headers: getAuthHeaders() }),
    sendWhatsApp: (data) => axios.post(`${API_BASE}/notifications/send-whatsapp`, data, { headers: getAuthHeaders() }),
    testWhatsApp: (data) => axios.post(`${API_BASE}/notifications/test-whatsapp`, data, { headers: getAuthHeaders() }),
    sendWhatsAppTemplate: (data) => axios.post(`${API_BASE}/notifications/send-whatsapp-template`, data, { headers: getAuthHeaders() }),
  },

  // Global ERP notification bell (all internal roles)
  erpNotifications: {
    get: () => axios.get(`${API_BASE}/notifications`, { headers: getAuthHeaders() }),
    getUnreadCount: () => axios.get(`${API_BASE}/notifications/unread-count`, { headers: getAuthHeaders() }),
    markRead: (id) => axios.post(`${API_BASE}/notifications/${id}/read`, {}, { headers: getAuthHeaders() }),
    markAllRead: () => axios.post(`${API_BASE}/notifications/read-all`, {}, { headers: getAuthHeaders() }),
  },

  // Wallet & Referral System
  wallet: {
    // Customer endpoints
    getBalance: () => axios.get(`${API_BASE}/wallet/balance`, { headers: getAuthHeaders() }),
    getTransactions: (params) => axios.get(`${API_BASE}/wallet/transactions`, { params, headers: getAuthHeaders() }),
    applyReferral: (code) => axios.post(`${API_BASE}/wallet/apply-referral`, { referral_code: code }, { headers: getAuthHeaders() }),
    calculateUsage: (orderAmount) => axios.post(`${API_BASE}/wallet/calculate-usage`, { order_amount: orderAmount }, { headers: getAuthHeaders() }),
    useBalance: (orderId, amount) => axios.post(`${API_BASE}/wallet/use`, { order_id: orderId, amount }, { headers: getAuthHeaders() }),
    // Admin endpoints
    getSettings: () => axios.get(`${API_BASE}/wallet/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/wallet/settings`, data, { headers: getAuthHeaders() }),
    getStats: () => axios.get(`${API_BASE}/wallet/admin/stats`, { headers: getAuthHeaders() }),
    getAllWallets: (params) => axios.get(`${API_BASE}/wallet/admin/users`, { params, headers: getAuthHeaders() }),
    getReferrals: (params) => axios.get(`${API_BASE}/wallet/admin/referrals`, { params, headers: getAuthHeaders() }),
    creditWallet: (userId, amount, reason) => axios.post(`${API_BASE}/wallet/admin/credit`, { user_id: userId, amount, reason }, { headers: getAuthHeaders() }),
    // Rewards processing
    processReferralBonus: (data) => axios.post(`${API_BASE}/wallet/process-referral-bonus`, data, { headers: getAuthHeaders() }),
    processCashback: (data) => axios.post(`${API_BASE}/wallet/process-cashback`, data, { headers: getAuthHeaders() }),
  },

  // Daily Expense Management
  expenses: {
    getDashboard: () => axios.get(`${API_BASE}/expenses/dashboard`, { headers: getAuthHeaders() }),
    getCategories: () => axios.get(`${API_BASE}/expenses/categories`, { headers: getAuthHeaders() }),
    createCategory: (data) => axios.post(`${API_BASE}/expenses/categories`, data, { headers: getAuthHeaders() }),
    getSettings: () => axios.get(`${API_BASE}/expenses/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/expenses/settings`, data, { headers: getAuthHeaders() }),
    getEntries: (params) => axios.get(`${API_BASE}/expenses/entries`, { params, headers: getAuthHeaders() }),
    getEntry: (id) => axios.get(`${API_BASE}/expenses/entries/${id}`, { headers: getAuthHeaders() }),
    create: (data) => axios.post(`${API_BASE}/expenses/entries`, data, { headers: getAuthHeaders() }),
    approve: (id, data) => axios.post(`${API_BASE}/expenses/entries/${id}/approve`, data, { headers: getAuthHeaders() }),
    uploadAttachment: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API_BASE}/expenses/upload`, formData, { headers: getAuthHeaders() });
    },
    getVarianceReport: (month) => axios.get(`${API_BASE}/expenses/reports/variance`, { params: { month }, headers: getAuthHeaders() }),
    getCashFlowReport: (params) => axios.get(`${API_BASE}/expenses/reports/cash-flow`, { params, headers: getAuthHeaders() }),
  },

  // Asset Management
  assets: {
    getTypes: () => axios.get(`${API_BASE}/assets/types`, { headers: getAuthHeaders() }),
    // Owned Assets
    createOwned: (data) => axios.post(`${API_BASE}/assets/owned`, data, { headers: getAuthHeaders() }),
    getOwned: (params) => axios.get(`${API_BASE}/assets/owned`, { params, headers: getAuthHeaders() }),
    getOwnedById: (id) => axios.get(`${API_BASE}/assets/owned/${id}`, { headers: getAuthHeaders() }),
    updateOwned: (id, data) => axios.put(`${API_BASE}/assets/owned/${id}`, data, { headers: getAuthHeaders() }),
    addMaintenance: (id, data) => axios.post(`${API_BASE}/assets/owned/${id}/maintenance`, data, { headers: getAuthHeaders() }),
    disposeAsset: (id, data) => axios.post(`${API_BASE}/assets/owned/${id}/dispose`, data, { headers: getAuthHeaders() }),
    // Rented Assets
    createRented: (data) => axios.post(`${API_BASE}/assets/rented`, data, { headers: getAuthHeaders() }),
    getRented: (params) => axios.get(`${API_BASE}/assets/rented`, { params, headers: getAuthHeaders() }),
    getRentedById: (id) => axios.get(`${API_BASE}/assets/rented/${id}`, { headers: getAuthHeaders() }),
    recordRentPayment: (id, data) => axios.post(`${API_BASE}/assets/rented/${id}/payment`, data, { headers: getAuthHeaders() }),
    returnRented: (id, data) => axios.post(`${API_BASE}/assets/rented/${id}/return`, data, { headers: getAuthHeaders() }),
    // Handover
    createHandover: (data) => axios.post(`${API_BASE}/assets/handover/request`, data, { headers: getAuthHeaders() }),
    getHandovers: (params) => axios.get(`${API_BASE}/assets/handover/requests`, { params, headers: getAuthHeaders() }),
    approveHandover: (id, data) => axios.post(`${API_BASE}/assets/handover/${id}/approve`, data, { headers: getAuthHeaders() }),
    issueAsset: (id, data) => axios.post(`${API_BASE}/assets/handover/${id}/issue`, data, { headers: getAuthHeaders() }),
    returnAsset: (id, data) => axios.post(`${API_BASE}/assets/handover/${id}/return`, data, { headers: getAuthHeaders() }),
    getEmployeeHoldings: (empId) => axios.get(`${API_BASE}/assets/employee/${empId}/holdings`, { headers: getAuthHeaders() }),
    // Reports
    getAssetRegister: () => axios.get(`${API_BASE}/assets/reports/register`, { headers: getAuthHeaders() }),
    getDepreciationReport: (year) => axios.get(`${API_BASE}/assets/reports/depreciation`, { params: { year }, headers: getAuthHeaders() }),
    getRentLiability: () => axios.get(`${API_BASE}/assets/reports/rent-liability`, { headers: getAuthHeaders() }),
  },

  // Holidays & Calendar
  holidays: {
    getTypes: () => axios.get(`${API_BASE}/holidays/types`, { headers: getAuthHeaders() }),
    getSettings: () => axios.get(`${API_BASE}/holidays/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/holidays/settings`, data, { headers: getAuthHeaders() }),
    create: (data) => axios.post(`${API_BASE}/holidays/`, data, { headers: getAuthHeaders() }),
    getAll: (params) => axios.get(`${API_BASE}/holidays/`, { params, headers: getAuthHeaders() }),
    getCalendar: (year) => axios.get(`${API_BASE}/holidays/calendar/${year}`, { headers: getAuthHeaders() }),
    delete: (id) => axios.delete(`${API_BASE}/holidays/${id}`, { headers: getAuthHeaders() }),
    // Overtime
    recordOvertime: (data) => axios.post(`${API_BASE}/holidays/overtime`, data, { headers: getAuthHeaders() }),
    getOvertime: (params) => axios.get(`${API_BASE}/holidays/overtime`, { params, headers: getAuthHeaders() }),
    approveOvertime: (id, data) => axios.post(`${API_BASE}/holidays/overtime/${id}/approve`, data, { headers: getAuthHeaders() }),
    // Salary Impact
    getSalaryImpact: (empId, month) => axios.get(`${API_BASE}/holidays/salary-impact/${empId}`, { params: { month }, headers: getAuthHeaders() }),
    getSalaryPreview: (month) => axios.get(`${API_BASE}/holidays/salary-preview/${month}`, { headers: getAuthHeaders() }),
    // Reports
    getYearlyReport: (year) => axios.get(`${API_BASE}/holidays/reports/yearly/${year}`, { headers: getAuthHeaders() }),
    getOvertimeSummary: (month) => axios.get(`${API_BASE}/holidays/reports/overtime-summary/${month}`, { headers: getAuthHeaders() }),
  },

  // AI Demand Forecasting
  forecast: {
    getDemandForecast: (days = 90) => axios.get(`${API_BASE}/forecast/demand`, { params: { days }, headers: getAuthHeaders() }),
    getOrderStats: () => axios.get(`${API_BASE}/forecast/stats`, { headers: getAuthHeaders() }),
  },

  // CMS - Content Management
  cms: {
    // Pages
    getPages: (status) => axios.get(`${API_BASE}/cms/pages`, { params: { status }, headers: getAuthHeaders() }),
    createPage: (data) => axios.post(`${API_BASE}/cms/pages`, data, { headers: getAuthHeaders() }),
    updatePage: (id, data) => axios.put(`${API_BASE}/cms/pages/${id}`, data, { headers: getAuthHeaders() }),
    deletePage: (id) => axios.delete(`${API_BASE}/cms/pages/${id}`, { headers: getAuthHeaders() }),
    // Banners
    getBanners: (position) => axios.get(`${API_BASE}/cms/banners`, { params: { position }, headers: getAuthHeaders() }),
    createBanner: (data) => axios.post(`${API_BASE}/cms/banners`, data, { headers: getAuthHeaders() }),
    updateBanner: (id, data) => axios.put(`${API_BASE}/cms/banners/${id}`, data, { headers: getAuthHeaders() }),
    deleteBanner: (id) => axios.delete(`${API_BASE}/cms/banners/${id}`, { headers: getAuthHeaders() }),
    // Menu
    getMenu: (location) => axios.get(`${API_BASE}/cms/menu`, { params: { location }, headers: getAuthHeaders() }),
    createMenuItem: (data) => axios.post(`${API_BASE}/cms/menu`, data, { headers: getAuthHeaders() }),
    updateMenuItem: (id, data) => axios.put(`${API_BASE}/cms/menu/${id}`, data, { headers: getAuthHeaders() }),
    deleteMenuItem: (id) => axios.delete(`${API_BASE}/cms/menu/${id}`, { headers: getAuthHeaders() }),
    // Blog
    getBlogPosts: (params) => axios.get(`${API_BASE}/cms/blog`, { params, headers: getAuthHeaders() }),
    createBlogPost: (data) => axios.post(`${API_BASE}/cms/blog`, data, { headers: getAuthHeaders() }),
    updateBlogPost: (id, data) => axios.put(`${API_BASE}/cms/blog/${id}`, data, { headers: getAuthHeaders() }),
    deleteBlogPost: (id) => axios.delete(`${API_BASE}/cms/blog/${id}`, { headers: getAuthHeaders() }),
    getBlogCategories: () => axios.get(`${API_BASE}/cms/blog/categories`, { headers: getAuthHeaders() }),
    // Contact Info
    getContactInfo: () => axios.get(`${API_BASE}/cms/contact-info`, { headers: getAuthHeaders() }),
    updateContactInfo: (data) => axios.put(`${API_BASE}/cms/contact-info`, data, { headers: getAuthHeaders() }),
    // SEO & Sitemap
    getSitemap: () => axios.get(`${API_BASE}/cms/sitemap`, { headers: getAuthHeaders() }),
    getSEOSettings: () => axios.get(`${API_BASE}/cms/seo`, { headers: getAuthHeaders() }),
    updateSEOSettings: (data) => axios.put(`${API_BASE}/cms/seo`, data, { headers: getAuthHeaders() }),
    // Media Library
    getMedia: (params) => axios.get(`${API_BASE}/cms/media`, { params, headers: getAuthHeaders() }),
    uploadMedia: (data) => axios.post(`${API_BASE}/cms/media`, data, { headers: getAuthHeaders() }),
    deleteMedia: (id) => axios.delete(`${API_BASE}/cms/media/${id}`, { headers: getAuthHeaders() }),
    // Testimonials
    getTestimonials: () => axios.get(`${API_BASE}/cms/testimonials`, { headers: getAuthHeaders() }),
    createTestimonial: (data) => axios.post(`${API_BASE}/cms/testimonials`, data, { headers: getAuthHeaders() }),
    updateTestimonial: (id, data) => axios.put(`${API_BASE}/cms/testimonials/${id}`, data, { headers: getAuthHeaders() }),
    deleteTestimonial: (id) => axios.delete(`${API_BASE}/cms/testimonials/${id}`, { headers: getAuthHeaders() }),
    // FAQ
    getFAQs: (category) => axios.get(`${API_BASE}/cms/faq`, { params: { category }, headers: getAuthHeaders() }),
    createFAQ: (data) => axios.post(`${API_BASE}/cms/faq`, data, { headers: getAuthHeaders() }),
    updateFAQ: (id, data) => axios.put(`${API_BASE}/cms/faq/${id}`, data, { headers: getAuthHeaders() }),
    deleteFAQ: (id) => axios.delete(`${API_BASE}/cms/faq/${id}`, { headers: getAuthHeaders() }),
    // Gallery
    getGallery: (params) => axios.get(`${API_BASE}/cms/gallery`, { params, headers: getAuthHeaders() }),
    addGalleryItem: (data) => axios.post(`${API_BASE}/cms/gallery`, data, { headers: getAuthHeaders() }),
    updateGalleryItem: (id, data) => axios.put(`${API_BASE}/cms/gallery/${id}`, data, { headers: getAuthHeaders() }),
    deleteGalleryItem: (id) => axios.delete(`${API_BASE}/cms/gallery/${id}`, { headers: getAuthHeaders() }),
    // Homepage Sections
    getHomepageSections: () => axios.get(`${API_BASE}/cms/homepage`, { headers: getAuthHeaders() }),
    createHomepageSection: (data) => axios.post(`${API_BASE}/cms/homepage`, data, { headers: getAuthHeaders() }),
    updateHomepageSection: (id, data) => axios.put(`${API_BASE}/cms/homepage/${id}`, data, { headers: getAuthHeaders() }),
    deleteHomepageSection: (id) => axios.delete(`${API_BASE}/cms/homepage/${id}`, { headers: getAuthHeaders() }),
    // Footer Settings
    getFooterSettings: () => axios.get(`${API_BASE}/cms/footer`, { headers: getAuthHeaders() }),
    updateFooterSettings: (data) => axios.put(`${API_BASE}/cms/footer`, data, { headers: getAuthHeaders() }),
  },

  // Branch Management
  branches: {
    getAll: (params) => axios.get(`${API_BASE}/branches`, { params, headers: getAuthHeaders() }),
    get: (id) => axios.get(`${API_BASE}/branches/${id}`, { headers: getAuthHeaders() }),
    create: (data) => axios.post(`${API_BASE}/branches`, data, { headers: getAuthHeaders() }),
    update: (id, data) => axios.put(`${API_BASE}/branches/${id}`, data, { headers: getAuthHeaders() }),
    delete: (id) => axios.delete(`${API_BASE}/branches/${id}`, { headers: getAuthHeaders() }),
    getStats: (id) => axios.get(`${API_BASE}/branches/${id}/stats`, { headers: getAuthHeaders() }),
    transferInventory: (fromId, toId, productId, qty) => 
      axios.post(`${API_BASE}/branches/${fromId}/transfer-inventory?target_branch_id=${toId}&product_id=${productId}&quantity=${qty}`, {}, { headers: getAuthHeaders() }),
  },

  // Payment Tracking
  payments: {
    getDashboard: (period) => axios.get(`${API_BASE}/payments/dashboard`, { params: { period }, headers: getAuthHeaders() }),
    getOrderPayments: (orderId) => axios.get(`${API_BASE}/payments/order/${orderId}`, { headers: getAuthHeaders() }),
    recordPayment: (orderId, data) => axios.post(`${API_BASE}/payments/order/${orderId}/record`, data, { headers: getAuthHeaders() }),
    createPaymentLink: (data) => axios.post(`${API_BASE}/payments/create-link`, data, { headers: getAuthHeaders() }),
    getCustomerPayments: (customerId) => axios.get(`${API_BASE}/payments/customer/${customerId}`, { headers: getAuthHeaders() }),
    getLedger: (params) => axios.get(`${API_BASE}/payments/ledger`, { params, headers: getAuthHeaders() }),
    getOutstanding: () => axios.get(`${API_BASE}/payments/outstanding`, { headers: getAuthHeaders() }),
  },

  // Product Configuration
  config: {
    getAll: () => axios.get(`${API_BASE}/config/all`, { headers: getAuthHeaders() }),
    addThickness: (data) => axios.post(`${API_BASE}/config/thickness`, data, { headers: getAuthHeaders() }),
    deleteThickness: (value) => axios.delete(`${API_BASE}/config/thickness/${value}`, { headers: getAuthHeaders() }),
    addGlassType: (data) => axios.post(`${API_BASE}/config/glass-types`, data, { headers: getAuthHeaders() }),
    deleteGlassType: (id) => axios.delete(`${API_BASE}/config/glass-types/${id}`, { headers: getAuthHeaders() }),
    addColor: (data) => axios.post(`${API_BASE}/config/colors`, data, { headers: getAuthHeaders() }),
    deleteColor: (id) => axios.delete(`${API_BASE}/config/colors/${id}`, { headers: getAuthHeaders() }),
  },

  // Transport Management
  transport: {
    // Settings
    getSettings: () => axios.get(`${API_BASE}/transport/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/transport/settings`, data, { headers: getAuthHeaders() }),
    // Distance & Cost
    calculateDistance: (location) => axios.post(`${API_BASE}/transport/calculate-distance`, location, { headers: getAuthHeaders() }),
    calculateCost: (data) => axios.post(`${API_BASE}/transport/calculate-cost`, data, { headers: getAuthHeaders() }),
    // Vehicles
    getVehicles: (status) => axios.get(`${API_BASE}/transport/vehicles`, { params: { status }, headers: getAuthHeaders() }),
    createVehicle: (data) => axios.post(`${API_BASE}/transport/vehicles`, data, { headers: getAuthHeaders() }),
    updateVehicle: (id, data) => axios.put(`${API_BASE}/transport/vehicles/${id}`, data, { headers: getAuthHeaders() }),
    deleteVehicle: (id) => axios.delete(`${API_BASE}/transport/vehicles/${id}`, { headers: getAuthHeaders() }),
    // Drivers
    getDrivers: (status) => axios.get(`${API_BASE}/transport/drivers`, { params: { status }, headers: getAuthHeaders() }),
    createDriver: (data) => axios.post(`${API_BASE}/transport/drivers`, data, { headers: getAuthHeaders() }),
    updateDriver: (id, data) => axios.put(`${API_BASE}/transport/drivers/${id}`, data, { headers: getAuthHeaders() }),
    deleteDriver: (id) => axios.delete(`${API_BASE}/transport/drivers/${id}`, { headers: getAuthHeaders() }),
    // Dispatch
    createDispatch: (data) => axios.post(`${API_BASE}/transport/dispatch`, data, { headers: getAuthHeaders() }),
    getDispatches: (params) => axios.get(`${API_BASE}/transport/dispatches`, { params, headers: getAuthHeaders() }),
    updateDispatchStatus: (id, status) => axios.patch(`${API_BASE}/transport/dispatches/${id}/status`, null, { params: { status }, headers: getAuthHeaders() }),
    // Dashboard
    getDashboard: () => axios.get(`${API_BASE}/transport/dashboard`, { headers: getAuthHeaders() }),
    // Rented Vehicles
    getRentedVehicles: () => axios.get(`${API_BASE}/transport/rented-vehicles`, { headers: getAuthHeaders() }),
    createRentedVehicle: (data) => axios.post(`${API_BASE}/transport/rented-vehicles`, data, { headers: getAuthHeaders() }),
    updateRentedVehicle: (id, data) => axios.put(`${API_BASE}/transport/rented-vehicles/${id}`, data, { headers: getAuthHeaders() }),
    deleteRentedVehicle: (id) => axios.delete(`${API_BASE}/transport/rented-vehicles/${id}`, { headers: getAuthHeaders() }),
    // Trip Tracking
    startTrip: (data) => axios.post(`${API_BASE}/transport/trips/start`, data, { headers: getAuthHeaders() }),
    endTrip: (tripId, data) => axios.post(`${API_BASE}/transport/trips/${tripId}/end`, data, { headers: getAuthHeaders() }),
    getTrips: (params) => axios.get(`${API_BASE}/transport/trips`, { params, headers: getAuthHeaders() }),
    // Charges Report
    getChargesReport: (params) => axios.get(`${API_BASE}/transport/charges-report`, { params, headers: getAuthHeaders() }),
    // Enhanced Cost Calculator
    calculateCostEnhanced: (data) => axios.post(`${API_BASE}/transport/calculate-cost-enhanced`, data, { headers: getAuthHeaders() }),
    // Fuel Log
    getFuelLogs: (params) => axios.get(`${API_BASE}/transport/fuel-logs`, { params, headers: getAuthHeaders() }),
    createFuelLog: (data) => axios.post(`${API_BASE}/transport/fuel-logs`, data, { headers: getAuthHeaders() }),
    updateFuelLog: (id, data) => axios.put(`${API_BASE}/transport/fuel-logs/${id}`, data, { headers: getAuthHeaders() }),
    deleteFuelLog: (id) => axios.delete(`${API_BASE}/transport/fuel-logs/${id}`, { headers: getAuthHeaders() }),
    getFuelSummary: (params) => axios.get(`${API_BASE}/transport/fuel-logs/summary`, { params, headers: getAuthHeaders() }),
    // Mileage Tracker
    getMileageTracker: (params) => axios.get(`${API_BASE}/transport/mileage-tracker`, { params, headers: getAuthHeaders() }),
  },

  // Admin Manuals (storefront /api router, not /api/erp)
  adminManuals: {
    getAll: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin-manuals`, { headers: getAuthHeaders() }),
    create: (data) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin-manuals`, data, { headers: getAuthHeaders() }),
    update: (id, data) => axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin-manuals/${id}`, data, { headers: getAuthHeaders() }),
    delete: (id) => axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin-manuals/${id}`, { headers: getAuthHeaders() }),
    trackView: (id) => axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin-manuals/${id}/view`, {}, { headers: getAuthHeaders() }),
  },

  // Rewards & Referrals
  rewards: {
    // Settings
    getSettings: () => axios.get(`${API_BASE}/rewards/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/rewards/settings`, data, { headers: getAuthHeaders() }),
    // Referral
    getMyReferralCode: () => axios.get(`${API_BASE}/rewards/my-referral-code`, { headers: getAuthHeaders() }),
    applyReferral: (code) => axios.post(`${API_BASE}/rewards/apply-referral`, null, { params: { referral_code: code }, headers: getAuthHeaders() }),
    // Balance
    getMyBalance: () => axios.get(`${API_BASE}/rewards/my-balance`, { headers: getAuthHeaders() }),
    getTransactions: (limit) => axios.get(`${API_BASE}/rewards/transactions`, { params: { limit }, headers: getAuthHeaders() }),
    redeemCredit: (data) => axios.post(`${API_BASE}/rewards/redeem`, data, { headers: getAuthHeaders() }),
    // Admin
    getUserBalance: (userId) => axios.get(`${API_BASE}/rewards/admin/user/${userId}/balance`, { headers: getAuthHeaders() }),
    adjustCredit: (data) => axios.post(`${API_BASE}/rewards/admin/adjust-credit`, data, { headers: getAuthHeaders() }),
    getAllReferrals: (status) => axios.get(`${API_BASE}/rewards/admin/referrals`, { params: { status }, headers: getAuthHeaders() }),
    getDashboard: () => axios.get(`${API_BASE}/rewards/admin/dashboard`, { headers: getAuthHeaders() }),
  },

  // Refunds & Cancellations
  refunds: {
    getSettings: () => axios.get(`${API_BASE}/refunds/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/refunds/settings`, data, { headers: getAuthHeaders() }),
    list: (status) => axios.get(`${API_BASE}/refunds/list`, { params: { status }, headers: getAuthHeaders() }),
    getPending: () => axios.get(`${API_BASE}/refunds/pending`, { headers: getAuthHeaders() }),
    cancelOrder: (orderId, data) => axios.post(`${API_BASE}/refunds/orders/${orderId}/cancel`, data, { headers: getAuthHeaders() }),
    release: (refundId, data) => axios.post(`${API_BASE}/refunds/${refundId}/release`, data, { headers: getAuthHeaders() }),
    reject: (refundId, note) => axios.post(`${API_BASE}/refunds/${refundId}/reject`, null, { params: { note }, headers: getAuthHeaders() }),
  },

  // GST Management
  gst: {
    // Settings
    getSettings: () => axios.get(`${API_BASE}/gst/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/gst/settings`, data, { headers: getAuthHeaders() }),
    // HSN Codes
    getHSNCodes: () => axios.get(`${API_BASE}/gst/hsn-codes`, { headers: getAuthHeaders() }),
    addHSNCode: (data) => axios.post(`${API_BASE}/gst/hsn-codes`, data, { headers: getAuthHeaders() }),
    deleteHSNCode: (code) => axios.delete(`${API_BASE}/gst/hsn-codes/${code}`, { headers: getAuthHeaders() }),
    // States
    getStates: () => axios.get(`${API_BASE}/gst/states`, { headers: getAuthHeaders() }),
    // Calculation
    calculateGST: (data) => axios.post(`${API_BASE}/gst/calculate`, data, { headers: getAuthHeaders() }),
    // Verification
    verifyGSTIN: (gstin) => axios.post(`${API_BASE}/gst/verify`, { gstin }, { headers: getAuthHeaders() }),
    // Public
    getCompanyInfo: () => axios.get(`${API_BASE}/gst/company-info`, { headers: getAuthHeaders() }),
  },

  // Job Work Module
  jobWork: {
    // Public
    getLabourRates: () => axios.get(`${API_BASE}/job-work/labour-rates`, { headers: getAuthHeaders() }),
    calculateCost: (items) => axios.post(`${API_BASE}/job-work/calculate`, items, { headers: getAuthHeaders() }),
    getDisclaimer: () => axios.get(`${API_BASE}/job-work/disclaimer`, { headers: getAuthHeaders() }),
    // Customer
    createOrder: (data) => axios.post(`${API_BASE}/job-work/orders`, data, { headers: getAuthHeaders() }),
    getMyOrders: () => axios.get(`${API_BASE}/job-work/my-orders`, { headers: getAuthHeaders() }),
    getOrderById: (id) => axios.get(`${API_BASE}/job-work/orders/${id}`, { headers: getAuthHeaders() }),
    initiatePayment: (id) => axios.post(`${API_BASE}/job-work/orders/${id}/initiate-payment`, {}, { headers: getAuthHeaders() }),
    verifyPayment: (id, data) => axios.post(`${API_BASE}/job-work/orders/${id}/verify-payment`, data, { headers: getAuthHeaders() }),
    setCashPreference: (id) => axios.post(`${API_BASE}/job-work/orders/${id}/set-cash-preference`, {}, { headers: getAuthHeaders() }),
    // Admin
    getOrders: (status) => axios.get(`${API_BASE}/job-work/orders${status ? `?status=${status}` : ''}`, { headers: getAuthHeaders() }),
    getDashboard: () => axios.get(`${API_BASE}/job-work/dashboard`, { headers: getAuthHeaders() }),
    updateStatus: (id, data) => axios.patch(`${API_BASE}/job-work/orders/${id}/status`, data, { headers: getAuthHeaders() }),
    markCashPayment: (id, data) => axios.post(`${API_BASE}/job-work/orders/${id}/cash-payment`, data, { headers: getAuthHeaders() }),
    getRevenueStats: (startDate, endDate) => axios.get(`${API_BASE}/job-work/revenue-stats?start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeaders() }),
    getSettings: () => axios.get(`${API_BASE}/job-work/settings`, { headers: getAuthHeaders() }),
    updateSettings: (data) => axios.put(`${API_BASE}/job-work/settings`, data, { headers: getAuthHeaders() }),
  },

  // Customer Master / Profile Module
  customerMaster: {
    // States
    getStates: () => axios.get(`${API_BASE}/customer-master/states`, { headers: getAuthHeaders() }),
    // Stats
    getStats: () => axios.get(`${API_BASE}/customer-master/stats`, { headers: getAuthHeaders() }),
    // CRUD
    create: (data) => axios.post(`${API_BASE}/customer-master/`, data, { headers: getAuthHeaders() }),
    list: (params) => axios.get(`${API_BASE}/customer-master/`, { params, headers: getAuthHeaders() }),
    get: (id) => axios.get(`${API_BASE}/customer-master/${id}`, { headers: getAuthHeaders() }),
    update: (id, data) => axios.put(`${API_BASE}/customer-master/${id}`, data, { headers: getAuthHeaders() }),
    deactivate: (id) => axios.patch(`${API_BASE}/customer-master/${id}/deactivate`, {}, { headers: getAuthHeaders() }),
    reactivate: (id) => axios.patch(`${API_BASE}/customer-master/${id}/reactivate`, {}, { headers: getAuthHeaders() }),
    // Shipping Addresses
    addShippingAddress: (customerId, data) => axios.post(`${API_BASE}/customer-master/${customerId}/shipping-addresses`, data, { headers: getAuthHeaders() }),
    updateShippingAddress: (customerId, addressId, data) => axios.put(`${API_BASE}/customer-master/${customerId}/shipping-addresses/${addressId}`, data, { headers: getAuthHeaders() }),
    deleteShippingAddress: (customerId, addressId) => axios.delete(`${API_BASE}/customer-master/${customerId}/shipping-addresses/${addressId}`, { headers: getAuthHeaders() }),
    // KYC
    updateKYC: (customerId, status, notes) => axios.patch(`${API_BASE}/customer-master/${customerId}/kyc?status=${status}${notes ? `&notes=${notes}` : ''}`, {}, { headers: getAuthHeaders() }),
    // Migration
    migrate: () => axios.post(`${API_BASE}/customer-master/migrate-existing`, {}, { headers: getAuthHeaders() }),
    // Search for invoicing
    searchForInvoice: (q) => axios.get(`${API_BASE}/customer-master/search/for-invoice`, { params: { q }, headers: getAuthHeaders() }),
  },

  // Lead Integration (IndiaMart, JustDial, GMB)
  leads: {
    getAll: (params) => axios.get(`${API_BASE}/leads`, { params, headers: getAuthHeaders() }),
    getStats: () => axios.get(`${API_BASE}/leads/stats`, { headers: getAuthHeaders() }),
    updateStatus: (id, data) => axios.put(`${API_BASE}/leads/${id}/status`, data, { headers: getAuthHeaders() }),
    assign: (id, data) => axios.put(`${API_BASE}/leads/${id}/assign`, data, { headers: getAuthHeaders() }),
    addFollowUp: (id, data) => axios.post(`${API_BASE}/leads/${id}/follow-up`, data, { headers: getAuthHeaders() }),
    getDetail: (id) => axios.get(`${API_BASE}/leads/${id}`, { headers: getAuthHeaders() }),
    create: (data) => axios.post(`${API_BASE}/leads`, data, { headers: getAuthHeaders() }),
  },

  // Customer-facing profile endpoints (no customer-master role gates)
  customerPortal: {
    getProfile: () => axios.get(`${API_BASE}/customer-portal/profile`, { headers: getAuthHeaders() }),
    updateProfile: (data) => axios.put(`${API_BASE}/customer-portal/profile`, data, { headers: getAuthHeaders() }),
    getNotifications: () => axios.get(`${API_BASE}/customer-portal/notifications`, { headers: getAuthHeaders() }),
    getNotificationsUnreadCount: () => axios.get(`${API_BASE}/customer-portal/notifications/unread-count`, { headers: getAuthHeaders() }),
    markNotificationRead: (id) => axios.post(`${API_BASE}/customer-portal/notifications/${id}/read`, {}, { headers: getAuthHeaders() }),
    getStates: () => axios.get(`${API_BASE}/gst/states`, { headers: getAuthHeaders() }),
  },
};

export default erpApi;
